import { action, computed, makeObservable, observable } from 'mobx'
import type { StreamSubscriptionActions } from 'tricklejs/dist/stream_subscription'
import { getGlobalAuthors } from '../firestore/GlobalAuthor'
import { GlobalAuthor } from '../models/GlobalAuthor'
import type { FirebaseRepository } from '../models/FirebaseRepository'

export class GlobalAuthorStore {
  repository: FirebaseRepository
  @observable private store = observable.array<GlobalAuthor>([], {
    deep: false,
  })

  timeout: NodeJS.Timeout | null = null

  subscription?: StreamSubscriptionActions

  constructor(repository: FirebaseRepository) {
    this.repository = repository
    makeObservable(this)
  }

  scheduleRecompute() {
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.recompute()
    }, 100)
  }

  recompute() {
    if (this.subscription) {
      this.subscription.cancel()
      this.subscription = undefined
    }

    if (!this.repository.isAuthenticated) return

    const authorIds = this.store
      .map((author) => author.id)
      .filter((id) => id !== '')
      .sort()

    if (authorIds.length === 0) return

    const stream = getGlobalAuthors(this.repository, { authorIds })

    this.subscription = stream.listen((models) => {
      this.updateStore(models)
    })
  }

  @action
  updateStore(models: GlobalAuthor[]) {
    for (const model of models) {
      const found = this.byId[model.id]
      if (found) {
        found.replaceDoc(model.doc)
      } else {
        model.changeLoadingState(false)
        this.store.push(model)
      }
    }
  }

  @action
  getAuthor(authorId: string) {
    const found = this.byId[authorId]
    if (found) return found

    const empty = GlobalAuthor.empty(this.repository, authorId)
    this.store.push(empty)
    this.scheduleRecompute()
    return empty
  }

  @action
  getAuthors(authorIds: string[]) {
    const array = []
    for (const authorId of authorIds) {
      const found = this.byId[authorId]
      if (found) {
        array.push(found)
      } else {
        const empty = GlobalAuthor.empty(this.repository, authorId)
        this.store.push(empty)
        this.scheduleRecompute()
        array.push(empty)
      }
    }
    return array
  }

  @computed
  get byId() {
    const map: Record<string, GlobalAuthor> = {}
    for (const author of this.store) {
      map[author.id] = author
    }
    return map
  }

  clear() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
    if (this.subscription) {
      this.subscription.cancel()
      this.subscription = undefined
    }
    this.store.clear()
  }
}
